import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import Navigation from '../components/Navigation';
import './AdminUsers.css';

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    uid: '',
    email: '',
    displayName: '',
    role: 'chapter_member',
    chapterId: ''
  });
  const [chapters, setChapters] = useState([]);

  useEffect(() => {
    fetchUsers();
    fetchChapters();
  }, []);

  async function fetchUsers() {
    try {
      const q = query(collection(db, 'users'), orderBy('email'));
      const querySnapshot = await getDocs(q);
      const usersList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      setLoading(false);
    }
  }

  async function fetchChapters() {
    try {
      const querySnapshot = await getDocs(collection(db, 'chapters'));
      const chaptersList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setChapters(chaptersList);
    } catch (error) {
      console.error('Error fetching chapters:', error);
    }
  }

  function handleInputChange(e) {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }

  function handleEdit(user) {
    setEditingUser(user);
    setFormData({
      uid: user.id,
      email: user.email || '',
      displayName: user.displayName || '',
      role: user.role || 'chapter_member',
      chapterId: user.chapterId || ''
    });
    setShowForm(true);
  }

  function handleCancel() {
    setShowForm(false);
    setEditingUser(null);
    setFormData({
      uid: '',
      email: '',
      displayName: '',
      role: 'chapter_member',
      chapterId: ''
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();

    try {
      const userData = {
        email: formData.email,
        displayName: formData.displayName,
        role: formData.role,
        chapterId: formData.chapterId || null
      };

      if (editingUser) {
        // Update existing user
        await updateDoc(doc(db, 'users', editingUser.id), userData);
        alert('User updated successfully!');
      } else {
        // Create new user profile (user must be created in Authentication first)
        if (!formData.uid) {
          alert('Please enter the User UID from Firebase Authentication');
          return;
        }
        await addDoc(collection(db, 'users'), {
          ...userData,
          uid: formData.uid
        });
        alert('User profile created successfully!');
      }

      handleCancel();
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      alert('Failed to save user. Please try again.');
    }
  }

  async function handleDelete(userId, userEmail) {
    if (!window.confirm(`Are you sure you want to delete ${userEmail}?\n\nNote: This only removes the user profile. You must also delete the user from Firebase Authentication manually.`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'users', userId));
      alert('User profile deleted successfully!\n\nRemember to also delete this user from Firebase Authentication.');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user.');
    }
  }

  const getRoleBadgeClass = (role) => {
    switch(role) {
      case 'admin': return 'role-admin';
      case 'president':
      case 'vp_projects':
      case 'vp_chapters':
      case 'treasurer': return 'role-leadership';
      default: return 'role-member';
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      'admin': 'Admin',
      'president': 'President',
      'vp_projects': 'VP Projects',
      'vp_chapters': 'VP Chapters',
      'treasurer': 'Treasurer',
      'chapter_member': 'Chapter Member'
    };
    return labels[role] || role;
  };

  return (
    <div className="admin-container">
      <Navigation />
      
      <div className="admin-content">
        <div className="admin-header">
          <h1>Manage Users ({users.length})</h1>
          <button onClick={() => setShowForm(!showForm)} className="create-button">
            {showForm ? 'Cancel' : '+ Add New User'}
          </button>
        </div>

        {showForm && (
          <div className="user-form-card">
            <h2>{editingUser ? 'Edit User' : 'Add New User'}</h2>
            
            {!editingUser && (
              <div className="info-box">
                <h4>⚠️ Important Steps:</h4>
                <ol>
                  <li>First, create the user in <strong>Firebase Authentication</strong></li>
                  <li>Copy the <strong>User UID</strong> from Authentication</li>
                  <li>Paste the UID below and fill in the details</li>
                  <li>Save to create the user profile</li>
                </ol>
              </div>
            )}

            <form onSubmit={handleSubmit} className="user-form">
              {!editingUser && (
                <div className="form-group">
                  <label>User UID (from Firebase Authentication) *</label>
                  <input
                    type="text"
                    name="uid"
                    value={formData.uid}
                    onChange={handleInputChange}
                    required
                    placeholder="Copy from Firebase Authentication Users tab"
                  />
                  <small>Example: xYz123AbC456DeF789...</small>
                </div>
              )}

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="user@tnfusa.org"
                  disabled={editingUser}
                />
              </div>

              <div className="form-group">
                <label>Display Name *</label>
                <input
                  type="text"
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleInputChange}
                  required
                  placeholder="John Doe"
                />
              </div>

              <div className="form-group">
                <label>Role *</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  required
                >
                  <option value="admin">Admin - Full Access</option>
                  <option value="president">President - Leadership</option>
                  <option value="vp_projects">VP Projects - Leadership</option>
                  <option value="vp_chapters">VP Chapters - Leadership</option>
                  <option value="treasurer">Treasurer - Leadership</option>
                  <option value="chapter_member">Chapter Member</option>
                </select>
              </div>

              {formData.role === 'chapter_member' && (
                <div className="form-group">
                  <label>Chapter</label>
                  <select
                    name="chapterId"
                    value={formData.chapterId}
                    onChange={handleInputChange}
                  >
                    <option value="">No Chapter Assigned</option>
                    {chapters.map(chapter => (
                      <option key={chapter.id} value={chapter.id}>
                        {chapter.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-actions">
                <button type="submit" className="submit-button">
                  {editingUser ? 'Update User' : 'Create User Profile'}
                </button>
                <button type="button" onClick={handleCancel} className="cancel-button">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="loading">Loading users...</div>
        ) : (
          <div className="table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Display Name</th>
                  <th>Role</th>
                  <th>Chapter</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td className="email-cell">{user.email}</td>
                    <td>{user.displayName || 'N/A'}</td>
                    <td>
                      <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                        {getRoleLabel(user.role)}
                      </span>
                    </td>
                    <td>
                      {user.chapterId ? (
                        chapters.find(c => c.id === user.chapterId)?.name || 'Unknown'
                      ) : (
                        <span className="no-chapter">No Chapter</span>
                      )}
                    </td>
                    <td className="actions-cell">
                      <button 
                        onClick={() => handleEdit(user)}
                        className="edit-button-small"
                        title="Edit"
                      >
                        ✏️ Edit
                      </button>
                      <button 
                        onClick={() => handleDelete(user.id, user.email)}
                        className="delete-button-small"
                        title="Delete"
                      >
                        🗑️ Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminUsers;
